import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import Head from "next/head";
import Script from "next/script";
import { Formsparkform } from "../../../../studio/sanity.types";
import styles from "./FormSparkForm.module.scss";
import { EffektButton, EffektButtonVariant } from "../../../shared/components/EffektButton/EffektButton";
import { RadioButton } from "../../../shared/components/RadioButton/RadioButton";
import { EffektCheckbox } from "../../../shared/components/EffektCheckbox/EffektCheckbox";

// Type definitions for form state
type FormState = Record<string, string | boolean>;

// Type guard to check if a field has options
const hasOptions = (field: any): field is {
  options: Array<{
    value: string;
    label: string;
  }>;
} => {
  return field.options && Array.isArray(field.options);
};
export const FormsparkForm: React.FC<{
  formData: Formsparkform;
}> = t0 => {
  const $ = _c(31);
  const {
    formData
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [formState, setFormState] = useState(t1);
  let t2;
  let t3;
  if ($[1] !== formData.fields) {
    t2 = () => {
      const initialState = {};
      formData.fields?.forEach(field => {
        if (field.field_type === "checkbox") {
          initialState[field.field_name || ""] = false;
        } else {
          initialState[field.field_name || ""] = "";
        }
      });
      setFormState(initialState);
    };
    t3 = [formData.fields];
    $[1] = formData.fields;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  React.useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = (fieldName, value) => {
      setFormState(prev => ({
        ...prev,
        [fieldName]: value
      }));
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const handleInputChange = t4;
  let t5;
  if ($[5] !== formState) {
    t5 = field_0 => {
      const fieldName_0 = field_0.field_name || "";
      const validation = field_0.validation || {};
      const commonProps = {
        name: fieldName_0,
        id: fieldName_0,
        required: validation.required
      };
      if (validation.min_length) {
        commonProps.minLength = validation.min_length;
      }
      if (validation.max_length) {
        commonProps.maxLength = validation.max_length;
      }
      if (validation.pattern) {
        commonProps.pattern = validation.pattern;
        if (validation.pattern_message) {
          commonProps.title = validation.pattern_message;
        }
      }
      if (field_0.autocomplete) {
        commonProps.autoComplete = field_0.autocomplete;
      }
      switch (field_0.field_type) {
        case "textarea":
          {
            return <textarea {...commonProps} value={formState[fieldName_0] as string || ""} onChange={e_2 => handleInputChange(fieldName_0, e_2.target.value)} placeholder={field_0.placeholder} rows={field_0.rows || 4} />;
          }
        case "select":
          {
            return <select {...commonProps} value={formState[fieldName_0] as string || ""} onChange={e_1 => handleInputChange(fieldName_0, e_1.target.value)}><option value="">Choose an option</option>{hasOptions(field_0) && field_0.options.map(_temp)}</select>;
          }
        case "radio":
          {
            return <div role="radiogroup" aria-labelledby={`${fieldName_0}-label`} className={styles.radioGroup}>{hasOptions(field_0) && field_0.options.map((option, index) => <div key={index}><RadioButton name={fieldName_0} selected={formState[fieldName_0] === option.value} onSelect={() => handleInputChange(fieldName_0, option.value)} title={option.label} data_cy={`${fieldName_0}-${index}`} required={index === 0 && (formState[fieldName_0] === "" || formState[fieldName_0] === undefined)} /></div>)}</div>;
          }
        case "checkbox":
          {
            return <div><EffektCheckbox required={commonProps.required} checked={!!formState[fieldName_0]} onChange={checked => handleInputChange(fieldName_0, checked)}>{field_0.field_label}</EffektCheckbox></div>;
          }
        case "number":
          {
            const numberProps = {
              ...commonProps
            };
            if (validation.min_value !== undefined) {
              numberProps.min = validation.min_value;
            }
            if (validation.max_value !== undefined) {
              numberProps.max = validation.max_value;
            }
            return <input {...numberProps} type="number" value={formState[fieldName_0] as string || ""} onChange={e_0 => handleInputChange(fieldName_0, e_0.target.value)} placeholder={field_0.placeholder} />;
          }
        default:
          {
            return <input {...commonProps} type={field_0.field_type || "text"} value={formState[fieldName_0] as string || ""} onChange={e => handleInputChange(fieldName_0, e.target.value)} placeholder={field_0.placeholder} />;
          }
      }
    };
    $[5] = formState;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const renderField = t5;
  if (!formData || !formData.fields) {
    let t6;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <div>No form data provided</div>;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  let t6;
  if ($[8] !== formData.turnstile_site_key) {
    t6 = formData.turnstile_site_key && <><Head><link rel="preconnect" href="https://challenges.cloudflare.com" /></Head><Script src="https://challenges.cloudflare.com/turnstile/v0/api.js" strategy="afterInteractive" /></>;
    $[8] = formData.turnstile_site_key;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `https://submit-form.com/${formData.form_id}`;
  const t8 = formData.submit_target || "_self";
  let t9;
  if ($[10] !== formData.fields || $[11] !== renderField) {
    let t10;
    if ($[13] !== renderField) {
      t10 = (field_1, index_1) => {
        const fieldName_1 = field_1.field_name || "";
        return <div key={index_1} className={styles.fieldWrapper}>{field_1.field_type !== "checkbox" && <label htmlFor={fieldName_1} id={`${fieldName_1}-label`}>{field_1.field_label}</label>}{renderField(field_1)}</div>;
      };
      $[13] = renderField;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    t9 = formData.fields.map(t10);
    $[10] = formData.fields;
    $[11] = renderField;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[15] !== formData.turnstile_site_key) {
    t10 = formData.turnstile_site_key && <div className="cf-turnstile" data-sitekey={formData.turnstile_site_key} />;
    $[15] = formData.turnstile_site_key;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== formData.honeypot_field) {
    t11 = formData.honeypot_field && <input type="text" name="_gotcha" style={{
      display: "none"
    }} tabIndex={-1} autoComplete="off" />;
    $[17] = formData.honeypot_field;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = formData.submit_button_text || "Submit";
  let t13;
  if ($[19] !== t12) {
    t13 = <EffektButton variant={EffektButtonVariant.PRIMARY} type="submit">{t12}</EffektButton>;
    $[19] = t12;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t13 || $[24] !== t7 || $[25] !== t8 || $[26] !== t9) {
    t14 = <div className={styles.wrapper}><form action={t7} method="POST" target={t8} className={styles.form}>{t9}{t10}{t11}{t13}</form></div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t13;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t14 || $[29] !== t6) {
    t15 = <>{t6}{t14}</>;
    $[28] = t14;
    $[29] = t6;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};
function _temp(option_0, index_0) {
  return <option key={index_0} value={option_0.value}>{option_0.label}</option>;
}